import React, { useState, useMemo } from "react";
import { Modal, Button, Tabs, Tab, ToggleButton, ButtonGroup } from 'react-bootstrap';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import * as DeviceStyle from "../styles/DeviceSettings.style";


// ===============================================
// 💡 TypeScript 인터페이스 정의 (수정/추가됨)
// ===============================================

// 1. 그래프 데이터의 개별 요소 타입 정의
interface ChartDataPoint {
    time: string;
    value: number;
}

// 충전/방전 데이터를 소스(Solar/Wind)별로 담는 구조
interface SourceData {
    solar: ChartDataPoint[];
    wind: ChartDataPoint[];
    led: ChartDataPoint[];
}

// 탭별 전체 데이터 구조
interface GraphData {
    charge: SourceData;
    discharge: SourceData;
}

// 필터링된 그래프에 표시될 데이터 구조 (Merged/Combined)
interface DisplayDataPoint {
    time: string;
    solarCharge?: number;
    windCharge?: number;
    ledCharge?: number;
    solarDischarge?: number;
    windDischarge?: number;
    [key: string]: string | number | undefined;
}

// 💡 일일 순간 발전 데이터 인터페이스
interface DailyDataItem {
    reg_time: string;
    device_id: number;
    wind_power_instant_generation: number;
    solar_power_instant_generation: number;
    LED_power_instant: number;
}

// 💡 7일 총 발전 데이터 인터페이스 (추가)
interface WeeklyDataItem {
    reg_date: string;
    device_id: number;
    wind_power_total_generation: number;
    solar_power_total_generation: number;
    LED_power_total: number;
}

// ===============================================
// 💡 MOCK_DATA 정의 (더미 방전 데이터는 유지)
// ===============================================

const MOCK_DATA: {
    dailyDischargeOnly: SourceData;
    weeklyChargeDischarge: GraphData;
} = {
    // 일일 방전 데이터는 임시로 Mock을 사용합니다.
    dailyDischargeOnly: {
        solar: [
            { time: '10:00', value: 15 }, { time: '13:00', value: 25 }, { time: '16:00', value: 20 },
        ],
        wind: [
            { time: '10:00', value: 5 }, { time: '13:00', value: 10 }, { time: '16:00', value: 8 },
        ],
        led: [
            { time: '10:00', value: 5 }, { time: '13:00', value: 10 }, { time: '16:00', value: 8 },
        ],        
    },

    weeklyChargeDischarge: {
        // 7일 충전 데이터는 이제 weeklyData prop으로 받으므로 Mock은 방전 데이터만 유지
        charge: {
            solar: [], // 빈 배열로 대체 (실제 데이터 우선)
            wind: [],  // 빈 배열로 대체 (실제 데이터 우선)
            led: [],
        },
        discharge: {
            solar: [
                { time: 'Day 1', value: 70 }, { time: 'Day 4', value: 90 }, { time: 'Day 7', value: 65 },
            ],
            wind: [
                { time: 'Day 1', value: 30 }, { time: 'Day 4', value: 50 }, { time: 'Day 7', value: 30 },
            ],
            led: [
                { time: 'Day 1', value: 30 }, { time: 'Day 4', value: 50 }, { time: 'Day 7', value: 30 },
            ],            
        },
    },
};

const EMPTY_SOURCE_DATA: SourceData = { solar: [], wind: [], led: [] };


// ===============================================
// 💡 유틸리티 함수: 1일 데이터 변환 (KST 변환 포함)
// ===============================================
const transformDailyData = (data: DailyDataItem[]): SourceData => {
    
    const aggregatedData = new Map<string, { solar: number, wind: number, led: number }>();
    const KST_OFFSET_MS = 9 * 60 * 60000; 

    data.forEach(item => {
        const date = new Date(item.reg_time);

        // KST 시간 타임스탬프 계산: UTC 타임스탬프 + KST 시차
        const kstTimestamp = date.getTime() + KST_OFFSET_MS;
        const kstDate = new Date(kstTimestamp);

        // KST Date 객체에서 HH:MM 추출
        const hours = String(kstDate.getUTCHours()).padStart(2, '0');
        const minutes = String(kstDate.getUTCMinutes()).padStart(2, '0');
        const timeKey = `${hours}:${minutes}`;

        
        if (!aggregatedData.has(timeKey)) {
            aggregatedData.set(timeKey, { solar: 0, wind: 0, led: 0 });
        }

        const current = aggregatedData.get(timeKey)!;
        current.solar += item.solar_power_instant_generation;
        current.wind += item.wind_power_instant_generation;
        current.led += item.LED_power_instant;
    });

    const solarData: ChartDataPoint[] = [];
    const windData: ChartDataPoint[] = [];
    const ledData: ChartDataPoint[] = [];

    aggregatedData.forEach((values, time) => {
        solarData.push({ time, value: values.solar }); 
        windData.push({ time, value: values.wind });
        ledData.push({ time, value: values.led });
    });
    
    solarData.sort((a, b) => a.time.localeCompare(b.time));
    windData.sort((a, b) => a.time.localeCompare(b.time));
    ledData.sort((a, b) => a.time.localeCompare(b.time));

    return { solar: solarData, wind: windData, led: ledData };
};


// ===============================================
// 💡 유틸리티 함수: 7일 데이터 변환 (추가됨)
// ===============================================
// const transformWeeklyData = (data: WeeklyDataItem[]): SourceData => {
    
//     // reg_date를 Date 객체로 파싱하고 날짜순으로 정렬
//     const sortedData = [...data].sort((a, b) => new Date(a.reg_date).getTime() - new Date(b.reg_date).getTime());
    
//     const solarData: ChartDataPoint[] = [];
//     const windData: ChartDataPoint[] = [];

//     // 'Day 1', 'Day 2' ... 레이블을 붙입니다.
//     sortedData.forEach((item, index) => {
        
//         const timeKey = `Day ${index + 1}`; 
        
//         solarData.push({ 
//             time: timeKey, 
//             value: item.solar_power_total_generation
//         });
        
//         windData.push({ 
//             time: timeKey, 
//             value: item.wind_power_total_generation
//         });
//     });

//     return { solar: solarData, wind: windData };
// };

const transformWeeklyData = (data: WeeklyDataItem[]): SourceData => {
    
    // reg_date를 Date 객체로 파싱하고 날짜순으로 정렬
    // 정렬은 UTC 기준으로 진행해도 무방합니다.
    const sortedData = [...data].sort((a, b) => new Date(a.reg_date).getTime() - new Date(b.reg_date).getTime());
    
    const solarData: ChartDataPoint[] = [];
    const windData: ChartDataPoint[] = [];
    const ledData: ChartDataPoint[] = [];

    // KST 시차 (9시간)를 밀리초로 변환합니다. (UTC+9)
    const KST_OFFSET_MS = 9 * 60 * 60000; 

    // 'MM-DD' 레이블을 추출합니다.
    sortedData.forEach((item) => {
        
        // 1. UTC 문자열을 Date 객체로 파싱
        const date = new Date(item.reg_date);

        // 2. KST 시간 타임스탬프 계산: UTC 타임스탬프 + KST 시차
        const kstTimestamp = date.getTime() + KST_OFFSET_MS;
        const kstDate = new Date(kstTimestamp);

        // 3. KST Date 객체에서 월(MM)과 일(DD)을 추출
        // getUTCMonth는 0부터 시작하므로 +1, padStart로 2자리 보장
        const month = String(kstDate.getUTCMonth() + 1).padStart(2, '0');
        const day = String(kstDate.getUTCDate()).padStart(2, '0');
        
        const timeKey = `${month}-${day}`; // 예: '10-21'
        
        // solarCharge 데이터
        solarData.push({ 
            time: timeKey, 
            value: item.solar_power_total_generation
        });
        
        // windCharge 데이터
        windData.push({ 
            time: timeKey, 
            value: item.wind_power_total_generation
        });

        // ledCharge 데이터
        ledData.push({ 
            time: timeKey, 
            value: item.LED_power_total
        });        
    });

    // 7일 데이터는 날짜순으로 정렬되었으므로 추가 정렬이 필요 없습니다.

    return { solar: solarData, wind: windData, led: ledData };
};

// ===============================================
// 데이터를 시간 기준으로 병합하는 유틸리티 함수
// ===============================================
const mergeDataForChart = (chargeData: SourceData, dischargeData: SourceData, isDaily: boolean): DisplayDataPoint[] => {
    const allTimes = new Set<string>();
    
    chargeData.solar.forEach(d => allTimes.add(d.time));
    chargeData.wind.forEach(d => allTimes.add(d.time));
    chargeData.led.forEach(d => allTimes.add(d.time));

    dischargeData.solar.forEach(d => allTimes.add(d.time));
    dischargeData.wind.forEach(d => allTimes.add(d.time));

    let sortedTimes = Array.from(allTimes);

    if (isDaily) {
        sortedTimes.sort((a, b) => {
            const [ah, am] = a.split(':').map(Number);
            const [bh, bm] = b.split(':').map(Number);
            return (ah * 60 + am) - (bh * 60 + bm);
        });
    } else {
        sortedTimes.sort((a, b) => {
            const aNum = parseInt(a.replace('Day ', ''), 10);
            const bNum = parseInt(b.replace('Day ', ''), 10);
            return aNum - bNum;
        });
    }

    const mergedData: { [key: string]: DisplayDataPoint } = {};
    
    sortedTimes.forEach(time => {
        mergedData[time] = { 
            time, 
            solarCharge: 0, 
            windCharge: 0, 
            ledCharge: 0, 
            solarDischarge: 0, 
            windDischarge: 0 
        };
    });

    // const updateMergedData = (data: ChartDataPoint[], source: 'solar' | 'wind', type: 'Charge' | 'Discharge') => {
    const updateMergedData = (data: ChartDataPoint[], source: 'solar' | 'wind' | 'led', type: 'Charge' | 'Discharge') => {  
        const dataKey = `${source}${type}`; 
        data.forEach(d => {
            if (mergedData[d.time]) {
                mergedData[d.time][dataKey] = d.value; 
            }
        });
    };

    updateMergedData(chargeData.solar, 'solar', 'Charge');
    updateMergedData(chargeData.wind, 'wind', 'Charge');
    updateMergedData(chargeData.led, 'led', 'Charge');
    // updateMergedData(dischargeData.solar, 'solar', 'Discharge');
    // updateMergedData(dischargeData.wind, 'wind', 'Discharge');

    return Object.values(mergedData);
};


// ===============================================
// 📊 DeviceGraphModal 컴포넌트 (데이터 적용 로직 수정)
// ===============================================

// 💡 DeviceGraphModalProps 인터페이스 수정
interface DeviceGraphModalProps {
    show: boolean;
    onClose: () => void;
    selectedGroupName: string | null;
    deviceNum: number;
    dailyData: DailyDataItem[]; 
    weeklyData: WeeklyDataItem[];
}


const DeviceGraphModal = ({ show, onClose, selectedGroupName, deviceNum, dailyData, weeklyData }: DeviceGraphModalProps) => {
    const [activeTab, setActiveTab] = useState('daily_charge_discharge');
    const [activeSource, setActiveSource] = useState('all');  
    
    const isDaily = activeTab === 'daily_charge_discharge';

    // 💡 1. 일일 순간 발전 데이터 변환
    const transformedDailyChargeData: SourceData = useMemo(() => {
        if (!dailyData || dailyData.length === 0) {
            return EMPTY_SOURCE_DATA;
        }
        return transformDailyData(dailyData);
    }, [dailyData]);

    // 💡 2. 7일 총 발전 데이터 변환 (추가)
    const transformedWeeklyChargeData: SourceData = useMemo(() => {
        if (!weeklyData || weeklyData.length === 0) {
            return EMPTY_SOURCE_DATA;
        }
        return transformWeeklyData(weeklyData);
    }, [weeklyData]);


    // 💡 3. 현재 탭에 맞는 충전 및 방전 데이터를 결정
    const currentCharge: SourceData = isDaily 
        ? transformedDailyChargeData // 일일 탭: 실제 dailyData 사용
        : transformedWeeklyChargeData; // 💡 7일 탭: 실제 weeklyData 사용
    
    const currentDischarge: SourceData = isDaily
        ? EMPTY_SOURCE_DATA // 일일 탭: 방전 데이터 제외
        : EMPTY_SOURCE_DATA; // 7일 탭: 더미 방전 데이터 사용
        
    // 4. 최종적으로 병합된 차트 데이터 생성
    const chartData = useMemo(() => 
        mergeDataForChart(currentCharge, currentDischarge, isDaily), 
        [currentCharge, currentDischarge, isDaily]
    );

    // 필터 버튼 정의 (기존과 동일)
    const sourceFilters = [
        { name: '전체', value: 'all' },
        { name: '태양광', value: 'solar' },
        { name: '풍력', value: 'wind' },
        { name: 'LED', value: 'led' },
    ];

    // 그래프 라인 정의 (기존과 동일)
    const lines = [
        { key: 'solarCharge', name: '태양광 발전량', color: '#f13838ff', active: activeSource === 'all' || activeSource === 'solar' },
        { key: 'windCharge', name: '풍력 발전량', color: '#00C49F', active: activeSource === 'all' || activeSource === 'wind' },
        { key: 'ledCharge', name: 'LED 소모량', color: '#9ca332ff', active: activeSource === 'all' || activeSource === 'led' },
        // 방전 라인이 필요하다면 주석을 해제하세요. (현재 7일 탭에만 Mock 방전 데이터가 있습니다)
        // { key: 'solarDischarge', name: '태양광 방전', color: '#FF8042', active: (!isDaily && (activeSource === 'all' || activeSource === 'solar')) },
        // { key: 'windDischarge', name: '풍력 방전', color: '#974cddff', active: (!isDaily && (activeSource === 'all' || activeSource === 'wind')) },
    ].filter(line => line.active);


    // 단일 그래프 렌더링 함수 (기존과 동일)
    const renderCombinedChart = (title: string, data: DisplayDataPoint[]) => (
        <div className="chart-wrapper">
            
            {/* 데이터 소스 필터 */}
            <div className="mb-3 d-flex justify-content-center">
                <ButtonGroup> 
                    {sourceFilters.map((radio, idx) => (
                        <ToggleButton
                            key={idx}
                            id={`toggle-source-${idx}`}
                            type="radio"
                            variant={activeSource === radio.value ? 'primary' : 'outline-secondary'}
                            name="radio"
                            value={radio.value}
                            checked={activeSource === radio.value}
                            onChange={(e) => setActiveSource(e.currentTarget.value)}
                        >
                            {radio.name}
                        </ToggleButton>
                    ))}
                </ButtonGroup>
            </div>

            <ResponsiveContainer width="100%" height={180}>
                <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2ceceff" /> 
                    <XAxis dataKey="time" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    
                    {lines.map(line => (
                        <Line
                            key={line.key}
                            type="monotone"
                            dataKey={line.key}
                            name={line.name}
                            stroke={line.color}
                            strokeWidth={3}
                            dot={false}
                            // 7일 탭에만 방전 라인 표시를 위해 조건부로 strokeDasharray 적용 (선택 사항)
                            strokeDasharray={line.key.includes('Discharge') ? "5 5" : "0 0"} 
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );


    return (
        <DeviceStyle.CustomSizeModal show={show} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>📊 {selectedGroupName} {deviceNum}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Tabs 
                    id="charge-discharge-tabs" 
                    activeKey={activeTab} 
                    onSelect={(k: string | null) => setActiveTab(k as string)} 
                    className="mb-3"
                >

                    {/* 1. 일일 순간 발전 탭 (실제 dailyData 적용)🔋 */}
                    <Tab eventKey="daily_charge_discharge" title="📉 일일 그래프">
                        {renderCombinedChart('일일 그래프', chartData)}
                    </Tab>

                    {/* 2. 7일 총 발전 탭 (실제 weeklyData 적용) */}
                    <Tab eventKey="weekly_charge_discharge" title="📈 7일 그래프">
                        {renderCombinedChart('7일 그래프', chartData)}
                    </Tab>

                </Tabs>
            </Modal.Body>

            <br></br>

            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    닫기
                </Button>
            </Modal.Footer>
        </DeviceStyle.CustomSizeModal>  
    );
};

export default DeviceGraphModal;